import { useState, useEffect } from "react";
import "./edit.css";
import { Button } from "react-bootstrap";
import { Box } from "@mui/material";
import { useHistory } from "react-router-dom";
import { REACT_APP_API_URL, API_ROUTES } from "config/api-config";
import { update_staff_notification } from "services/api-service";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Select from "react-select";
import { Search, X, Download } from "lucide-react";
import { downloadStaffData } from "services/downloadUtils";
import { MdArrowBack } from "react-icons/md";
export default function EditStaffDetails() {
  const theme = useTheme();
  const [staff, setStaff] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // ✅ FIXED: Track ID for high-res modal fetching, not a Base64 string
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const history = useHistory();

  const [filters, setFilters] = useState({ name: "", department: "", id: "", email: "" });

  useEffect(() => {
    fetchStaff();
  }, []);

  const fetchStaff = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const baseUrl = REACT_APP_API_URL || "http://127.0.0.1:8000";
      const safeUrl = `${baseUrl}/${API_ROUTES.get_all_staff}`.replace(/([^:]\/)\/+/g, "$1");

      const response = await fetch(safeUrl, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error("Failed to fetch");
      const data = await response.json();
      
      setStaff(data);
      setFilteredStaff(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching staff:", error);
      toast.error("Failed to fetch staff data");
      setLoading(false);
    }
  };

  useEffect(() => { applyFilters(); }, [staff, filters]);

  const applyFilters = () => {
    let filtered = staff;
    if (filters.name.trim()) filtered = filtered.filter((s) => s.first_name?.toLowerCase().includes(filters.name.toLowerCase()) || s.last_name?.toLowerCase().includes(filters.name.toLowerCase()));
    if (filters.department.trim()) filtered = filtered.filter((s) => s.department?.toLowerCase().includes(filters.department.toLowerCase()));
    if (filters.id.trim()) filtered = filtered.filter((s) => s.staff_id.toString().includes(filters.id));
    if (filters.email.trim()) filtered = filtered.filter((s) => s.email?.toLowerCase().includes(filters.email.toLowerCase()));
    setFilteredStaff(filtered);
  };

  const clearAllFilters = () => {
    setFilters({ name: "", department: "", id: "", email: "" });
    setFilteredStaff(staff);
  };

  const handleDeleteStaff = async (staff_id) => {
    if (window.confirm(`Are you sure you want to delete staff ${staff_id}?`)) {
      try {
        const token = localStorage.getItem("accessToken");
        const baseUrl = REACT_APP_API_URL || "http://127.0.0.1:8000";
        const safeUrl = `${baseUrl}/${API_ROUTES.delete_staff}/${staff_id}`.replace(/([^:]\/)\/+/g, "$1");
        
        await fetch(safeUrl, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchStaff();
        toast.success("Staff deleted successfully");
      } catch (error) {
        toast.error("Failed to delete staff");
      }
    }
  };

  const handleEditStaff = (staff_id) => {
    history.push(`/admin/editstaff/${staff_id}`, { staff_id });
  };

  const handleNotificationToggle = async (staff_id, currentStatus) => {
    const newStatus = !currentStatus;

    setFilteredStaff(prev => prev.map(s => 
      s.staff_id === staff_id ? { ...s, is_notification: newStatus } : s
    ));
    setStaff(prev => prev.map(s => 
      s.staff_id === staff_id ? { ...s, is_notification: newStatus } : s
    ));

    try {
      await update_staff_notification(staff_id, newStatus);
      if (newStatus) {
        toast.success("🔔 Notifications ENABLED for this staff member");
      } else {
        toast.warn("🔕 Notifications DISABLED for this staff member");
      }
    } catch (error) {
      console.error("Failed to update notification status:", error);
      toast.error("Failed to save setting. Reverting...");
      fetchStaff(); 
    }
  };

  const customSelectStyles = {
    control: (provided) => ({ ...provided, minWidth: "150px", borderRadius: "8px", border: "2px solid #ddd", padding: "2px", fontSize: "14px" }),
  };

  return (
    <main className="emotionContainer">
      <div style={{ width: "100%", justifyContent: "center", fontFamily: "-moz-initial" }}>
        
        {/* BACK BUTTON */}
        <button 
          onClick={() => history.push("/admin/FaceDetails")}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#6b7280',
            cursor: 'pointer',
            fontSize: '15px',
            fontWeight: '600',
            marginBottom: '20px',
            padding: 0
          }}
        >
          <MdArrowBack size={20} />
          Back to Menu
        </button>
        {/* DOWNLOAD SECTION */}
        <div style={{ display: "flex", gap: "12px", alignItems: "center", padding: "15px 20px", backgroundColor: "#f0f7ff", borderRadius: "8px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          <Download size={20} style={{ color: "#0066cc" }} />
          <h3 style={{ margin: 0, color: "#333", fontSize: "16px" }}>Download Report</h3>
          <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
            <Button disabled={downloading} onClick={() => downloadStaffData(filteredStaff, "excel")} variant="success" size="sm">📊 Excel</Button>
            <Button disabled={downloading} onClick={() => downloadStaffData(filteredStaff, "pdf")} variant="danger" size="sm">📄 PDF</Button>
          </div>
        </div>

        {/* FILTER SECTION */}
        <div style={{ backgroundColor: "#f8f9fa", padding: "15px 20px", borderRadius: "8px", marginBottom: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "12px" }}>
            <Search size={20} style={{ color: "#666" }} />
            <h3 style={{ margin: 0, color: "#333" }}>Search & Filter Staff</h3>
          </div>
          
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "12px" }}>
            <input type="text" placeholder="Search by Name" value={filters.name} onChange={(e) => setFilters({ ...filters, name: e.target.value })} style={{ width: "100%", padding: "8px", borderRadius: "6px", border: "2px solid #ddd" }} />
            <Select styles={customSelectStyles} options={[]} isClearable placeholder="Department..." />
          </div>
        </div>

        {/* TABLE */}
        <table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Staff ID</th>
              <th>First Name</th>
              <th>Last Name</th>
              <th>Department</th>
              <th>Notify</th> 
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredStaff.map((s) => (
              <tr key={s.staff_id}>
                <td>
                  {/* ✅ FIXED: Use has_image and API Route with robust URL formatting */}
                  {s.has_image ? (
                    <img
                      src={`${REACT_APP_API_URL}/${API_ROUTES.get_staff_image(s.staff_id)}`.replace(/([^:]\/)\/+/g, "$1")}
                      alt="Staff"
                      loading="lazy"
                      onMouseOver={(e) => e.target.style.transform = "scale(2.5)"}
                      onMouseOut={(e) => e.target.style.transform = "scale(1)"}
                      onClick={() => setSelectedImageId(s.staff_id)}
                      style={{ cursor: "pointer", width: "50px", height: "50px", objectFit: "cover", borderRadius: "4px", transition: "transform 0.2s ease" }}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  ) : (
                    <div style={{ width: "50px", height: "50px", display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0", borderRadius: "4px", color: "#999", fontSize: "10px", textAlign: "center" }}>
                      No<br/>Photo
                    </div>
                  )}
                </td>
                <td>{s.staff_id}</td>
                <td>{s.first_name}</td>
                <td>{s.last_name}</td>
                <td>{s.department}</td>
                
                <td>
                  <input 
                    type="checkbox" 
                    checked={s.is_notification || false} 
                    onChange={() => handleNotificationToggle(s.staff_id, s.is_notification)}
                    style={{ width: "18px", height: "18px", cursor: "pointer", marginLeft: "10px" }}
                  />
                </td>

                <td style={{ display: "flex", gap: "5px" }}>
                  <button onClick={() => handleEditStaff(s.staff_id)} className="btn btn-primary btn-sm">Edit</button>
                  <button onClick={() => handleDeleteStaff(s.staff_id)} className="btn btn-danger btn-sm">Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* ✅ FIXED: Modal hits API endpoint using selectedImageId */}
        {selectedImageId && (
          <div onClick={() => setSelectedImageId(null)} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
            <img 
              src={`${REACT_APP_API_URL}/${API_ROUTES.get_staff_image(selectedImageId)}`.replace(/([^:]\/)\/+/g, "$1")} 
              alt="Enlarged" 
              style={{ width: "30%", height: "auto", borderRadius: "8px", objectFit: "contain" }} 
            />
          </div>
        )}
      </div>
    </main>
  );
}
